import React from 'react';
import { TransactionBuilderAccountSelectionPage } from '../transaction-builder/TransactionBuilderAccountSelectionPage';
import { TransactionBuilderNetworkSelectionPage } from '../transaction-builder/TransactionBuilderNetworkSelectionPage';
import { TransactionBuilderCartPage } from '../transaction-builder/TransactionBuilderCartPage';
import { TransactionBuilderDescriptionPage } from '../transaction-builder/TransactionBuilderDescriptionPage';
import { TransactionBuilderReviewPage } from '../transaction-builder/TransactionBuilderReviewPage';
import { TransactionBuilderJsonImportPage } from '../transaction-builder/TransactionBuilderJsonImportPage';
import {
  useTransactionBuilderStore,
  TransactionBuilderPageType,
} from '@/components/transaction-builder/useTransactionBuilderStore';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TransactionBuilderWalletConnectProvider } from '@/components/transaction-builder/wallet-connect/TransactionBuilderWalletConnectProvider';

/**
 * Transaction Builder page component
 * Orchestrates the multi-step transaction building flow
 * Each page component renders its own header and footer
 */
export function TransactionBuilderPage(): React.ReactElement {
  const currentPage = useTransactionBuilderStore((state) => state.currentPage);
  const account = useTransactionBuilderStore((state) => state.account);
  const network = useTransactionBuilderStore((state) => state.network);

  return (
    <TransactionBuilderWalletConnectProvider>
      <ScrollArea className="h-[100dvh] bg-muted font-[Inter,sans-serif]">
        <div className="flex flex-col items-stretch w-full min-h-full pb-28">
          {(() => {
            switch (currentPage.page) {
              case TransactionBuilderPageType.ACCOUNT_SELECTION_PAGE:
                return <TransactionBuilderAccountSelectionPage />;
              case TransactionBuilderPageType.NETWORK_SELECTION_PAGE:
                return <TransactionBuilderNetworkSelectionPage />;
              case TransactionBuilderPageType.POLICY_SELECTION_PAGE:
              case TransactionBuilderPageType.TRANSACTION_DETAILS_PAGE:
              case TransactionBuilderPageType.CART_PAGE:
              case TransactionBuilderPageType.JSON_IMPORT_PAGE:
                // Case: policy selection and JSON import both need a parent source account.
                if (!account) {
                  return <TransactionBuilderAccountSelectionPage />;
                }
                // Case: the parent's network must be selected before validating imported children.
                if (network === undefined) {
                  return <TransactionBuilderNetworkSelectionPage />;
                }
                // Case: JSON import assigns policies per child under the selected account and network.
                if (
                  currentPage.page ===
                  TransactionBuilderPageType.JSON_IMPORT_PAGE
                ) {
                  return (
                    <TransactionBuilderJsonImportPage
                      key={`${account.id}:${network}`}
                      account={account}
                      network={network}
                    />
                  );
                }
                return (
                  <TransactionBuilderCartPage
                    key={`${account.id}:${network}`}
                    account={account}
                    network={network}
                  />
                );
              case TransactionBuilderPageType.DESCRIPTION_PAGE:
                return <TransactionBuilderDescriptionPage />;
              case TransactionBuilderPageType.REVIEW_PAGE:
                return <TransactionBuilderReviewPage />;
            }
          })()}
        </div>
      </ScrollArea>
    </TransactionBuilderWalletConnectProvider>
  );
}
